import React, { useState, useMemo, useRef, useEffect } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/pagination'

import { useSelector, useDispatch } from 'react-redux'
import { loadStatusMap } from '../../store/slices/watchlist.slice'
import { Link } from 'react-router-dom'
import { animeListImages } from '../../services/images.service'
import { getAnimeDetailsUrl, getAnimeImage } from '../../services/util.service'
import { AnimeWatchlistEdit } from '../UserProfile/UserWatchlist/AnimeWatchlistEdit'
import { ReviewPopup } from '../ReviewSystem/ReviewPopup'
import { useWatchlistActions } from '../../hooks/useWatchlistActions'
import { EditPen } from '../ui/icons/user'
import { MinusIcon, PlusIcon, ArrowLeft, ArrowRight } from '../ui/icons/shared'
import { Button } from '../ui/elements'

export function SimilarAnimeSlider({ animes }) {
  const dispatch = useDispatch()
  const loggedInUser = useSelector((s) => s.userModule.loggedInUser)
  const userId = loggedInUser?._id
  const statusMap = useSelector((s) => s.watchlist.statusMap[userId] || {})
  const reviews = useSelector((s) => s.reviewModule.reviews) || []

  // למנוע לולאת קריאות מיותרות
  const loadedRef = useRef(new Set())

  useEffect(() => {
    if (!userId || !animes?.length) return
    const missingIds = animes
      .map((a) => a.mal_id)
      .filter((id) => !statusMap[id] && !loadedRef.current.has(id))
    if (missingIds.length) {
      dispatch(
        loadStatusMap({
          userId,
          listType: 'watchlist',
          anizoneIds: missingIds,
        })
      )
      missingIds.forEach((id) => loadedRef.current.add(id))
    }
  }, [dispatch, userId, animes])

  const [isHovered, setIsHovered] = useState({})
  const [isPlusHovered, setIsPlusHovered] = useState({})
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [editData, setEditData] = useState({})
  const [editItem, setEditItem] = useState(null)
  const [reviewPopupOpen, setReviewPopupOpen] = useState(false)
  const [initialReviewData, setInitialReviewData] = useState(null)
  const swiperRef = useRef(null)

  const {
    handleAddItem,
    handleSaveChanges,
    handleRemoveItem,
    getUpdatedWatchItem,
  } = useWatchlistActions(userId)

  const userAlreadyReviewed = useMemo(
    () => (animeId) =>
      reviews.find((r) => r.animeId === animeId && r.userId === userId),
    [reviews, userId]
  )

  const handleOpenReviewPopup = (animeId) => {
    const review = userAlreadyReviewed(animeId)
    setInitialReviewData(review || null)
    setReviewPopupOpen(true)
  }

  // פונקציות עם גלילה חלקה ל־5 סליידים
  const handleNext = () => {
    const swiper = swiperRef.current
    if (!swiper) return
    const current = swiper.activeIndex
    const max = animes.length - 1
    const nextIndex = Math.min(current + 5, max)
    swiper.slideTo(nextIndex, 600)
  }

  const handlePrev = () => {
    const swiper = swiperRef.current
    if (!swiper) return
    const current = swiper.activeIndex
    const prevIndex = Math.max(current - 5, 0)
    swiper.slideTo(prevIndex, 600)
  }

  if (!animes?.length) return null

  return (
    <div className="w-full px-2 py-4">
      <div className="flex justify-between items-center my-4">
        <h3 className="text-h3 self-end">אולי תאהב גם...</h3>

        <div className="flex flex-row-reverse items-center gap-2  justify-start">
          <button
            className="transform transition-transform duration-300 hover:scale-105"
            onClick={handleNext}
            title="הקודם"
            aria-label="הקודם"
            type="button"
          >
            <ArrowLeft />
          </button>
          <button
            className="transform transition-transform duration-300 hover:scale-105"
            onClick={handlePrev}
            title="הבא"
            aria-label="הבא"
            type="button"
          >
            <ArrowRight />
          </button>
          <div className="transform transition-transform duration-300 hover:scale-105">
            <Button
              to="/animes"
              text="לכל האנימות"
              width={150}
              height={60}
              fontSize="text-lg"
              textColor="text-buttonSecondary1"
              bgColor="fill-accentLight" // רק צבע SVG (למשל fill-blue-500)
              aria-label="מעבר לעמוד כל האנימות באניזון"
              title="צפה ברשימת כל האנימות באניזון"
            />
          </div>
        </div>
      </div>
      <Swiper
        slidesPerView={2}
        spaceBetween={16}
        breakpoints={{
          640: { slidesPerView: 3 },
          768: { slidesPerView: 4 },
          1024: { slidesPerView: 6 },
        }}
        className="!pb-8"
        onSwiper={(swiper) => (swiperRef.current = swiper)}
      >
        {animes.map((anime) => {
          // עדיין תמיד בדוק מול Redux statusMap
          const existingItem = statusMap[anime.mal_id]
            ? { anizone_id: anime.mal_id, ...statusMap[anime.mal_id] }
            : null

          const titleHebrew = anime.title_hebrew || ''
          const titleEnglish = anime.title_english || anime.title || ''
          const altTitle = `${titleHebrew} // ${titleEnglish}`
          const imgAlt =
            titleHebrew && titleEnglish
              ? `${titleEnglish} (${titleHebrew}) poster`
              : `${titleEnglish || titleHebrew} poster`

          return (
            <SwiperSlide key={anime.mal_id}>
              <div
                className="flex flex-col items-center relative transition-transform duration-300 hover:scale-105"
                onMouseEnter={() =>
                  setIsHovered((prev) => ({ ...prev, [anime.mal_id]: true }))
                }
                onMouseLeave={() => {
                  setIsHovered((prev) => ({ ...prev, [anime.mal_id]: false }))
                  setIsPlusHovered((prev) => ({
                    ...prev,
                    [anime.mal_id]: false,
                  }))
                }}
                itemScope
                itemType="https://schema.org/TVSeries"
              >
                <meta itemProp="name" content={titleEnglish || titleHebrew} />
                <div className="relative w-full h-[240px] sm:h-[280px] md:h-[320px]">
                  <Link
                    to={getAnimeDetailsUrl(anime)}
                    className="block w-full h-full"
                    title={altTitle}
                    aria-label={`לפרטי אנימה: ${altTitle}`}
                    itemProp="url"
                  >
                    <img
                      src={getAnimeImage(anime)}
                      alt={imgAlt}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-300"
                      itemProp="image"
                    />
                  </Link>
                  {/* עריכת Watchlist */}
                  {existingItem && isHovered[anime.mal_id] ? (
                    <button
                      onClick={() => {
                        setEditData({
                          progress: existingItem.progress,
                          status: existingItem.status,
                          start_date: existingItem.start_date,
                          finish_date: existingItem.finish_date,
                        })
                        setEditItem(existingItem)
                        setIsEditOpen(true)
                      }}
                      title="ערוך סטטוס צפייה"
                      aria-label="ערוך סטטוס צפייה"
                      className="absolute bottom-16 left-2 z-20 w-10 h-10 bg-accent rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300"
                    >
                      <EditPen />
                    </button>
                  ) : (
                    !existingItem && (
                      <div
                        className={`absolute bottom-16 left-2 z-20 flex gap-2 transition-opacity duration-300 ${
                          isHovered[anime.mal_id]
                            ? 'opacity-100 scale-105'
                            : 'opacity-0 scale-95'
                        }`}
                        onMouseEnter={() =>
                          setIsPlusHovered((prev) => ({
                            ...prev,
                            [anime.mal_id]: true,
                          }))
                        }
                        onMouseLeave={() =>
                          setIsPlusHovered((prev) => ({
                            ...prev,
                            [anime.mal_id]: false,
                          }))
                        }
                      >
                        <button
                          className="w-10 h-10 bg-accent rounded-full flex items-center justify-center transition-transform duration-300 hover:scale-110"
                          title="הוסף ל־Watching"
                          aria-label="הוסף ל־Watching"
                          onClick={() => handleAddItem('Watching', anime)}
                        >
                          {isPlusHovered[anime.mal_id] ? (
                            <MinusIcon />
                          ) : (
                            <PlusIcon />
                          )}
                        </button>
                        <div
                          className={`absolute bottom-10 left-1/2 transform -translate-x-1/2 p-2 rounded-lg flex flex-col gap-1 transition-all duration-300 ${
                            isPlusHovered[anime.mal_id]
                              ? 'opacity-100 scale-105'
                              : 'opacity-0 scale-95'
                          }`}
                        >
                          <button
                            title="הוסף לרשימת צפייה"
                            aria-label="הוסף ל־Watching"
                            className="w-10 h-10 bg-accent rounded-full flex items-center justify-center transition-transform duration-300 hover:scale-110"
                            onClick={() => handleAddItem('Watching', anime)}
                          >
                            <img
                              src={animeListImages.addToWatch}
                              alt="אייקון הוספה לצפייה"
                              className="w-4 h-4"
                            />
                          </button>
                          <button
                            title="הוסף ל־Plan to Watch"
                            aria-label="הוסף ל־Plan to Watch"
                            className="w-10 h-10 bg-accent rounded-full flex items-center justify-center transition-transform duration-300 hover:scale-110"
                            onClick={() =>
                              handleAddItem('Plan to Watch', anime)
                            }
                          >
                            <img
                              src={animeListImages.planToWatch}
                              alt="אייקון תכנון צפייה"
                              className="w-4 h-4"
                            />
                          </button>
                        </div>
                      </div>
                    )
                  )}
                  {/* שם האנימה */}
                  <Link
                    to={getAnimeDetailsUrl(anime)}
                    className="absolute bottom-0 left-0 w-full z-10"
                    title={altTitle}
                    aria-label={`כותרת: ${altTitle}`}
                  >
                    <div className="bg-secondaryBackground bg-opacity-75 text-center p-2">
                      <p
                        className="text-white rtl-truncate max-w-full overflow-hidden whitespace-nowrap text-ellipsis"
                        title={altTitle}
                      >
                        <span lang="he" itemProp="alternateName">
                          {titleHebrew}
                        </span>{' '}
                        //{' '}
                        <span lang="en" itemProp="name">
                          {titleEnglish}
                        </span>
                      </p>
                    </div>
                  </Link>
                </div>
              </div>
            </SwiperSlide>
          )
        })}
      </Swiper>

      {/* Dialogs לפעולות */}
      {isEditOpen && editItem && (
        <AnimeWatchlistEdit
          item={editItem}
          editData={editData}
          setEditData={setEditData}
          handleCloseEdit={() => setIsEditOpen(false)}
          handleSaveItemChanges={handleSaveChanges}
          getUpdatedWatchItem={getUpdatedWatchItem}
          handleOpenReviewPopup={handleOpenReviewPopup}
          userAlreadyReviewed={userAlreadyReviewed}
          onRemove={() => {
            handleRemoveItem(editItem.anizone_id)
            setIsEditOpen(false)
          }}
        />
      )}

      {reviewPopupOpen && editItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <ReviewPopup
            animeId={editItem.anizone_id}
            initialData={initialReviewData}
            loggedInUser={loggedInUser}
            onClose={() => setReviewPopupOpen(false)}
          />
        </div>
      )}
    </div>
  )
}
